import { Box, LinearProgress, Skeleton, Stack, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useCommonApi } from '../../api';
import { DEFAULT_LOCALE } from '../../constants';
import { DealerOnly, dashboardCardSx } from '../common';
import { PeriodProgress } from '../types/statistics';

const monthName = (month: number) =>
  new Intl.DateTimeFormat(DEFAULT_LOCALE, { month: 'long' }).format(new Date(2000, month - 1, 1));

export const formatPercent = (value: number) =>
  `${new Intl.NumberFormat(DEFAULT_LOCALE, { maximumFractionDigits: 1 }).format(value)} %`;

export const getPeriodLabel = ({ periodKey, months }: Pick<PeriodProgress, 'periodKey' | 'months'>) =>
  `Období ${periodKey} – ${monthName(months[0])} až ${monthName(months[months.length - 1])}`;

const PeriodProgressBar = ({ tiers, count }: Pick<PeriodProgress, 'tiers' | 'count'>) => {
  // Limits are close together (e.g. 174/186/198), so markers are spaced evenly and progress is piecewise linear.
  const position = (index: number) => ((index + 1) / tiers.length) * 100;
  const reachedIndex = tiers.findIndex((tier) => count < tier.limit);
  const progress =
    reachedIndex === -1
      ? 100
      : (() => {
          const from =
            reachedIndex === 0
              ? { limit: 0, pos: 0 }
              : { limit: tiers[reachedIndex - 1].limit, pos: position(reachedIndex - 1) };
          const to = { limit: tiers[reachedIndex].limit, pos: position(reachedIndex) };
          return from.pos + ((count - from.limit) / (to.limit - from.limit)) * (to.pos - from.pos);
        })();

  return (
    <Box sx={{ pt: 1, pb: 5, px: 3 }}>
      <Box sx={{ position: 'relative' }}>
        <LinearProgress variant="determinate" value={progress} sx={{ height: 10, borderRadius: 5 }} />
        {tiers.map((tier, index) => (
          <Box
            key={tier.key}
            sx={{
              position: 'absolute',
              top: 0,
              left: `${position(index)}%`,
              transform: 'translateX(-50%)',
              height: 10
            }}
          >
            <Box sx={{ width: 2, height: 10, backgroundColor: 'text.primary', mx: 'auto' }} />
            <Typography
              variant="caption"
              fontWeight={tier.reached ? 700 : 400}
              color={tier.reached ? 'primary' : 'text.secondary'}
              sx={{
                position: 'absolute',
                top: 14,
                left: '50%',
                transform: 'translateX(-50%)',
                whiteSpace: 'nowrap',
                textAlign: 'center'
              }}
            >
              {formatPercent(tier.percentage)}
              <br />
              {tier.limit}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export const PeriodProgressWidget = ({ progress }: { progress: PeriodProgress }) => {
  const { months, monthCounts, count, currentPercentage, nextTier, tiers, settlementMonth } = progress;

  return (
    <Box sx={{ ...dashboardCardSx, height: 1, display: 'flex', flexDirection: 'column', p: { xs: 2, md: 3 }, gap: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={2} flexWrap="wrap">
        <Stack>
          <Typography variant="h6" fontWeight={600}>
            Kvartální odměna
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {getPeriodLabel(progress)}
          </Typography>
        </Stack>
        <Stack alignItems="flex-end">
          <Typography variant="body2" color="text.secondary">
            Aktuální sazba
          </Typography>
          <Typography color="primary" fontWeight={700} fontSize="1.5rem" lineHeight={1.2}>
            {currentPercentage > 0 ? formatPercent(currentPercentage) : 'zatím nedosaženo'}
          </Typography>
        </Stack>
      </Stack>
      <Stack direction="row" gap={3} flexWrap="wrap" alignItems="baseline">
        <Typography fontSize="2rem" fontWeight={600} lineHeight={1}>
          {count}{' '}
          <Typography component="span" variant="body2" color="text.secondary">
            nehod za období
          </Typography>
        </Typography>
        {months.map((month) => {
          const key = Object.keys(monthCounts).find((k) => Number(k.split('-')[1]) === month);
          return (
            <Typography key={month} variant="body2" color="text.secondary">
              {monthName(month)}: <b>{key ? monthCounts[key] : 0}</b>
            </Typography>
          );
        })}
      </Stack>
      <PeriodProgressBar tiers={tiers} count={count} />
      <Typography fontWeight={600}>
        {nextTier
          ? `Ještě ${nextTier.remaining} nehod do sazby ${formatPercent(nextTier.percentage)}`
          : 'Dosažena nejvyšší sazba'}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        Odměna se vyplácí v měsíci {monthName(settlementMonth)}. Stav je orientační, nehody se počítají podle data
        dealerského výkazu.
      </Typography>
    </Box>
  );
};

const PeriodProgressWidgetConnectedContent = () => {
  const { data, isLoading } = useQuery(useCommonApi().getPeriodProgress());

  if (isLoading) return <Skeleton variant="rounded" height={260} />;
  if (!data || !data.allowed || data.tiers.length === 0) return null;
  return <PeriodProgressWidget progress={data} />;
};

export const PeriodProgressWidgetConnected = () => (
  <DealerOnly>
    <PeriodProgressWidgetConnectedContent />
  </DealerOnly>
);
